import { Directive, ElementRef, Input, SimpleChanges } from '@angular/core';
import {
  animationFrames, EMPTY, endWith, finalize, fromEvent, map, merge,
  switchMap, takeUntil, takeWhile, tap
} from 'rxjs';

@Directive({
  selector: '[appInertiaSlider]',
})
export class InertiaSliderDirective {
  @Input('appInertiaSliderDirection') direction: 'horizontal' | 'vertical' | 'both' = 'horizontal';

  static initialized = false;
  static get BASIC_SELECTOR() { return 'app-inertia-slider' as const; }
  static get DIRECTION_SELECTOR() { return `${InertiaSliderDirective.BASIC_SELECTOR}-direction` as const; }

  constructor(private el: ElementRef<HTMLElement>) {
    this.el.nativeElement.setAttribute(InertiaSliderDirective.BASIC_SELECTOR, '');
    this.el.nativeElement.setAttribute(InertiaSliderDirective.DIRECTION_SELECTOR, this.direction);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.el.nativeElement.setAttribute(
      InertiaSliderDirective.DIRECTION_SELECTOR,
      changes['direction'].currentValue
    );
  }

  ngOnInit() {
    if (InertiaSliderDirective.initialized) return;
    InertiaSliderDirective.initialized = true;

    const style = document.createElement('style');
    style.innerText = `
      [${InertiaSliderDirective.DIRECTION_SELECTOR}="horizontal"] { overflow-x: auto; }
      [${InertiaSliderDirective.DIRECTION_SELECTOR}="vertical"]   { overflow-y: auto; }
      [${InertiaSliderDirective.DIRECTION_SELECTOR}="both"]       { overflow: auto; }

      [${InertiaSliderDirective.BASIC_SELECTOR}] {
        scroll-behavior: smooth;
        -ms-overflow-style: none; scrollbar-width: none;
      }
      [${InertiaSliderDirective.BASIC_SELECTOR}]::-webkit-scrollbar { display: none; }

      [${InertiaSliderDirective.BASIC_SELECTOR}] {
        user-select: none; cursor: grab;
      }
      [${InertiaSliderDirective.BASIC_SELECTOR}="moving"] {
        scroll-behavior: unset !important; scroll-snap-type: unset !important; cursor: grabbing;
      }

      [${InertiaSliderDirective.BASIC_SELECTOR}] * { user-drag: none; }
      /* garante que os links continuam clicáveis */
      [${InertiaSliderDirective.BASIC_SELECTOR}] a { pointer-events: auto; }
    `;
    document.head.appendChild(style);
  }

  ngAfterViewInit() {
    const slider: HTMLElement = this.el.nativeElement;

    // ---- NOVO: controle de clique vs. arraste ----
    const vector = Object.seal({ x: 0, y: 0 });
    let dragging = false;
    let startX = 0, startY = 0;
    let downTarget: HTMLElement | null = null;
    const CLICK_THRESHOLD = 6; // px

    const rawPointerDown$ = fromEvent<PointerEvent>(slider, 'pointerdown');
    const rawPointerMove$ = fromEvent<PointerEvent>(slider, 'pointermove');
    const rawPointerUp$   = merge(
      fromEvent<PointerEvent>(slider, 'pointerup'),
      fromEvent<PointerEvent>(slider, 'pointercancel')
    );

    const pointerDown$ = rawPointerDown$.pipe(
      tap((startEvent) => {
        if (startEvent.button !== 0 || startEvent.pointerType !== 'mouse') return;

        // estado inicial para detectar clique
        dragging = false;
        startX = startEvent.clientX;
        startY = startEvent.clientY;
        downTarget = startEvent.target as HTMLElement;

        requestAnimationFrame(() => {
          this.el.nativeElement.setAttribute(InertiaSliderDirective.BASIC_SELECTOR, 'moving');
        });
      }),
      tap((startEvent) => slider.setPointerCapture(startEvent.pointerId))
    );

    // ---- pointerUp decide entre "clique" ou "inércia" ----
    const pointerUp$ = rawPointerUp$.pipe(
      tap((endEvent) => {
        slider.releasePointerCapture(endEvent.pointerId);

        if (!dragging) {
          // tratar como CLIQUE: re-despachar para o link/RouterLink
          const el = (endEvent.target as HTMLElement)
            ?? (document.elementFromPoint(endEvent.clientX, endEvent.clientY) as HTMLElement | null);
          const anchor = (downTarget?.closest('a, [routerLink]') ??
                          el?.closest('a, [routerLink]')) as HTMLElement | null;
          if (anchor) {
            anchor.click(); // dispara handler do RouterLink
          }
          // reset visual e estado; não iniciar inércia
          this.el.nativeElement.setAttribute(InertiaSliderDirective.BASIC_SELECTOR, '');
          vector.x = 0; vector.y = 0;
        }
      }),
      // Se não houver arraste, não seguimos para a animação
      switchMap(() => dragging ? this.inertia$(slider, vector) : EMPTY),
      finalize(() => {
        // garantir reset no fim da animação
        this.el.nativeElement.setAttribute(InertiaSliderDirective.BASIC_SELECTOR, '');
      })
    );

    const pointerMove$ = rawPointerMove$.pipe(takeUntil(pointerUp$));

    pointerDown$
      .pipe(
        switchMap((startEvent) => {
          if (startEvent.button !== 0 || startEvent.pointerType !== 'mouse') return EMPTY;
          return pointerMove$;
        }),
        map((event) => {
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (!dragging && (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD)) {
            dragging = true;
          }
          vector.x = event.movementX;
          vector.y = event.movementY;
          return vector;
        }),
        tap((velocity) => {
          if (this.direction !== 'horizontal') slider.scrollTop  -= velocity.y;
          if (this.direction !== 'vertical')   slider.scrollLeft -= velocity.x;
        })
      )
      .subscribe();
  }

  // ---- EXTRAÍDO: animação de inércia quando houve arraste ----
  private inertia$(slider: HTMLElement, vector: { x: number; y: number; }) {
    const DURATION = 1000;
    const deceleration = Object.freeze({ x: vector.x, y: vector.y });
    let lastTime = 0;

    return animationFrames().pipe(
      takeUntil(fromEvent<PointerEvent>(slider, 'pointerdown')),
      map(({ elapsed }) => ({
        elapsed,
        deltaTime: (elapsed - lastTime) / 1000,
        progress: elapsed / DURATION,
      })),
      tap(({ elapsed }) => (lastTime = elapsed)),
      takeWhile(({ progress }) => progress < 1),
      endWith({ elapsed: 0, deltaTime: 0, progress: 1 }),
      map(({ deltaTime }) => {
        if (!deltaTime) { vector.x = 0; vector.y = 0; return vector; }
        if (this.direction !== 'horizontal') vector.y -= deceleration.y * deltaTime;
        if (this.direction !== 'vertical')   vector.x -= deceleration.x * deltaTime;
        return vector;
      }),
      tap((velocity) => {
        if (this.direction !== 'horizontal') slider.scrollTop  -= velocity.y;
        if (this.direction !== 'vertical')   slider.scrollLeft -= velocity.x;
      })
    );
  }
}
